import { Close } from "@mui/icons-material";
import type { SelectChangeEvent } from "@mui/material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomDateRangePicker from "../../../../components/CustomDateRangePicker";
import { useDistinctUsers } from "../../../../data/hooks/useDistinctUsers";
import { useLensProtocols } from "../../../../data/hooks/useLensProtocols";
import type { RankingResultCount, RankingTimeframe } from "../../../../types/callRanking";
import {
  ALL_AGENTS,
  RANKING_RESULT_COUNT_OPTIONS,
  RANKING_SUBMIT_LABELS,
  RANKING_TIMEFRAME_OPTIONS,
  RankingOrder
} from "../../../../types/callRanking";
import { isValidRankingResultCount } from "../../../../utils/callRanking";
import { CONVERSATION_LENS } from "../../../../utils/terminology";
import { getTimeRangeLabel } from "../../../../utils/timerange";
import { useSelectedGroups } from "../../../auth/hooks/useSelectedGroups";
import { useLensAttributes } from "../../hooks/useLensAttributes";
import { FindBestWorstCallsState, useFindBestWorstCallsStore } from "../../stores/findBestWorstCallsStore";
import { buildExploreUrl } from "./exploreUrl";
import LensAttributeSelector from "./LensAttributeSelector";
import OrderSelector from "./OrderSelector";
import SplitButton from "./SplitButton";

const getTimeframeDisplayValue = (timeframe: string): string =>
  timeframe.startsWith("custom:") ? "custom" : timeframe;

const getSubmitLabel = (order: RankingOrder | null, lens: string | null): string => {
  if (!order) {
    return "Select a ranking focus to continue";
  }
  if (lens === null) {
    return `Select a ${CONVERSATION_LENS.toLowerCase()} to continue`;
  }
  return RANKING_SUBMIT_LABELS[order];
};

interface FindBestWorstCallsWizardProps {
  open: boolean;
  onClose: () => void;
}

type WizardStore = FindBestWorstCallsState;

interface BuildUrlParams {
  order: RankingOrder | null;
  lens: string | null;
  attribute: string | null;
  timeframe: string;
  agent: string;
  resultCount: RankingResultCount;
}

const buildUrl = ({ order, lens, attribute, timeframe, agent, resultCount }: BuildUrlParams): string =>
  buildExploreUrl({
    order: order!,
    lens: lens!,
    attribute: attribute ?? undefined,
    timeframe,
    agent: agent !== ALL_AGENTS ? agent : undefined,
    resultCount
  });

interface TimeframeSelectorProps {
  timeframe: string;
  onChange: (e: SelectChangeEvent<string>) => void;
}

const TimeframeSelector = ({ timeframe, onChange }: TimeframeSelectorProps): React.ReactElement => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Timeframe</Typography>
    <Select
      fullWidth
      size="small"
      value={getTimeframeDisplayValue(timeframe)}
      onChange={onChange}
      renderValue={() => getTimeRangeLabel(timeframe)}
    >
      {RANKING_TIMEFRAME_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
      <MenuItem value="custom">Custom range...</MenuItem>
    </Select>
  </Box>
);

interface AgentSelectorProps {
  agent: string;
  users: string[];
  isLoadingUsers: boolean;
  onChange: (value: string) => void;
}

const AgentSelector = ({ agent, users, isLoadingUsers, onChange }: AgentSelectorProps): React.ReactElement => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Agent</Typography>
    <Select
      fullWidth
      size="small"
      value={agent}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoadingUsers}
      startAdornment={isLoadingUsers ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
    >
      <MenuItem value={ALL_AGENTS}>All agents</MenuItem>
      {users.map((u) => (
        <MenuItem key={u} value={u}>{u}</MenuItem>
      ))}
    </Select>
  </Box>
);

interface ResultCountSelectorProps {
  resultCount: RankingResultCount;
  order: RankingOrder | null;
  onChange: (value: RankingResultCount) => void;
}

const ResultCountSelector = ({ resultCount, order, onChange }: ResultCountSelectorProps): React.ReactElement => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Result count</Typography>
    <Select
      fullWidth
      size="small"
      value={resultCount}
      onChange={(e) => {
        const numValue = Number(e.target.value);
        if (isValidRankingResultCount(numValue)) {
          onChange(numValue);
        }
      }}
    >
      {RANKING_RESULT_COUNT_OPTIONS.map((n) => (
        <MenuItem key={n} value={n}>
          {order === RankingOrder.WorstFirst ? "Bottom" : "Top"} {n}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

interface WizardHandlers {
  handleDialogClose: () => void;
  handlePrimarySubmit: () => void;
  handleSecondarySubmit: () => void;
  handleTimeframeChange: (e: SelectChangeEvent<string>) => void;
  handleCustomDateRangeApply: (startDate: Date, endDate: Date) => void;
}

interface BuildHandlersParams {
  store: WizardStore;
  navigate: ReturnType<typeof useNavigate>;
  onClose: () => void;
  setIsCustomDateDialogOpen: (open: boolean) => void;
}

const buildWizardHandlers = ({ store, navigate, onClose, setIsCustomDateDialogOpen }: BuildHandlersParams): WizardHandlers => {
  const handleDialogClose = (): void => {
    store.resetWizard();
    onClose();
  };
  const urlParams = {
    order: store.order, lens: store.lens, attribute: store.attribute,
    timeframe: store.timeframe, agent: store.agent, resultCount: store.resultCount
  };
  return {
    handleDialogClose,
    handlePrimarySubmit: () => {
      if (!store.isValid()) {
        return;
      }
      navigate(buildUrl(urlParams));
      handleDialogClose();
    },
    handleSecondarySubmit: () => {
      if (!store.isValid()) {
        return;
      }
      window.open(buildUrl(urlParams), "_blank");
    },
    handleTimeframeChange: (e) => {
      const value = e.target.value;
      if (value === "custom") {
        setIsCustomDateDialogOpen(true);
        return;
      }
      store.setTimeframe(value as RankingTimeframe);
    },
    handleCustomDateRangeApply: (startDate, endDate) => {
      const customTimeRange = `custom:${format(startDate, "yyyy-MM-dd")}:${format(endDate, "yyyy-MM-dd")}`;
      store.setTimeframe(customTimeRange);
      setIsCustomDateDialogOpen(false);
    }
  };
};

interface WizardContentProps {
  store: WizardStore;
  lensProtocols: ReturnType<typeof useLensProtocols>["data"];
  attributes: ReturnType<typeof useLensAttributes>["data"];
  users: string[];
  isLoadingLenses: boolean;
  isLoadingAttributes: boolean;
  isLoadingUsers: boolean;
  handlers: WizardHandlers;
}

const WizardContent = (props: WizardContentProps): React.ReactElement => {
  const { store, lensProtocols, attributes, users, isLoadingLenses, isLoadingAttributes, isLoadingUsers, handlers } = props;
  return (
    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
      <OrderSelector value={store.order} onChange={store.setOrder} />
      <LensAttributeSelector
        lens={store.lens}
        attribute={store.attribute}
        lensProtocols={lensProtocols ?? []}
        attributes={attributes ?? []}
        isLoadingLenses={isLoadingLenses}
        isLoadingAttributes={isLoadingAttributes}
        onLensChange={store.setLens}
        onAttributeChange={store.setAttribute}
      />
      <TimeframeSelector timeframe={store.timeframe} onChange={handlers.handleTimeframeChange} />
      <AgentSelector agent={store.agent} users={users} isLoadingUsers={isLoadingUsers} onChange={store.setAgent} />
      <ResultCountSelector resultCount={store.resultCount} order={store.order} onChange={store.setResultCount} />
      <SplitButton
        label={getSubmitLabel(store.order, store.lens)}
        disabled={!store.isValid()}
        onPrimaryClick={handlers.handlePrimarySubmit}
        onSecondaryClick={handlers.handleSecondarySubmit}
      />
    </DialogContent>
  );
};

const WizardTitle = ({ onClose }: { onClose: () => void }): React.ReactElement => (
  <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
    <Box>
      <Typography variant="h6" fontWeight={600}>Find Best & Worst Calls</Typography>
      <Typography variant="body2" color="text.secondary">
        Pinpoint your strongest and weakest calls for any lens or attribute
      </Typography>
    </Box>
    <IconButton onClick={onClose} size="small">
      <Close />
    </IconButton>
  </DialogTitle>
);

interface DatePickerDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  timeframe: string;
  setTimeframe: WizardStore["setTimeframe"];
  onApply: (startDate: Date, endDate: Date) => void;
}

const DatePickerDialog = ({ open, setOpen, timeframe, setTimeframe, onApply }: DatePickerDialogProps): React.ReactElement => (
  <CustomDateRangePicker
    open={open}
    onClose={() => setOpen(false)}
    onApply={onApply}
    onApplyShortcut={(shortcut) => {
      setTimeframe(shortcut);
      setOpen(false);
    }}
    onClear={() => {
      setTimeframe("last7Days");
      setOpen(false);
    }}
    initialStartDate={null}
    initialEndDate={null}
    currentTimeRange={timeframe}
  />
);

const FindBestWorstCallsWizard = ({ open, onClose }: FindBestWorstCallsWizardProps): React.ReactElement => {
  const navigate = useNavigate();
  const { permittedEmails } = useSelectedGroups();
  const [isCustomDateDialogOpen, setIsCustomDateDialogOpen] = useState(false);
  const store = useFindBestWorstCallsStore();

  const { data: lensProtocols = [], isLoading: isLoadingLenses } = useLensProtocols({ timeRange: "allTime", permittedEmails });
  const { data: attributes = [], isLoading: isLoadingAttributes } = useLensAttributes({ lensName: store.lens });
  const { data: users = [], isLoading: isLoadingUsers } = useDistinctUsers({ timeRange: "allTime", permittedEmails });

  const handlers = buildWizardHandlers({ store, navigate, onClose, setIsCustomDateDialogOpen });

  return (
    <>
      <Dialog
        open={open}
        onClose={handlers.handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <WizardTitle onClose={handlers.handleDialogClose} />
        <WizardContent
          store={store}
          lensProtocols={lensProtocols}
          attributes={attributes}
          users={users}
          isLoadingLenses={isLoadingLenses}
          isLoadingAttributes={isLoadingAttributes}
          isLoadingUsers={isLoadingUsers}
          handlers={handlers}
        />
      </Dialog>
      <DatePickerDialog
        open={isCustomDateDialogOpen}
        setOpen={setIsCustomDateDialogOpen}
        timeframe={store.timeframe}
        setTimeframe={store.setTimeframe}
        onApply={handlers.handleCustomDateRangeApply}
      />
    </>
  );
};

export default FindBestWorstCallsWizard;
